import { useEffect, useMemo, useState } from 'react';

import { useSites } from '../../../hooks/queries/useSites';

/**
 * The site whose local day the visitor "Today" views are scoped to.
 *
 * The today endpoints require a site: "today" is a wall-clock question about
 * one building, and a district spanning timezones has no single answer. This
 * page had no site state at all, so it needs one before those reads can run.
 *
 * Seeds to the first site alphabetically so the view is populated on open,
 * and exposes the option list so a picker can change it.
 */
export function useVisitorSiteScope() {
  const { data: sitesData, isLoading } = useSites({ page_size: 100 });

  const siteOptions = useMemo(
    () =>
      [...(sitesData?.results ?? [])]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((s) => ({ label: s.name, value: String(s.id) })),
    [sitesData],
  );

  const [siteId, setSiteId] = useState<number | undefined>(undefined);

  // Seed once the list arrives, and re-seed if the current selection is no
  // longer in the list (site removed, or the user's access changed).
  useEffect(() => {
    if (!siteOptions.length) return;
    const stillPresent =
      siteId != null && siteOptions.some((o) => o.value === String(siteId));
    if (!stillPresent) setSiteId(Number(siteOptions[0].value));
  }, [siteOptions, siteId]);

  return {
    siteId,
    setSiteId,
    siteOptions,
    /** Queries must stay disabled until a real site is known — the endpoints 400 without one. */
    hasSite: siteId != null,
    isLoading,
  };
}
